import { nanoid } from "@reduxjs/toolkit";
import { Euler, MathUtils, Vector2, Vector3 } from "three";
import { t } from "../../../localization";
import { clearViewerTooltip, setViewerTooltip } from "../../viewer";
import { CustomMouseEvent, PanoControls } from "../controls";
import { Viewer } from "../main";
import { SceneCoordinate } from "../projections";

export enum MarkupOperation {
  // NOTE: These are using keys for translation
  SELECT="point-markup.new-selection",
  CLASSIFICATION="point-markup.update-classification",
  VISIBILITY="point-markup.hide-points"
}

export interface MarkupOperationInfo {
  ids: string[]
  operation: MarkupOperation
  description: string
  lastClassification?: number
}

class ClippingBlock {
  public id = nanoid();
  private markupList: PointMarkupList;
  private start = new Vector2();
  private finish = new Vector2();
  private position: Vector3;
  private target: Vector3;
  private rotation: Euler;
  private windowSize: Vector2;
  private focalLength = 0.0;
  private boundingBox: Number[] = null;
  public selected = true;
  private newClassification = 0.0;
  private pointsHidden = false;
  private reclassifyPoints = false;
  protected ignorePoint = false;
  private groupID: number;

  constructor(markupList: PointMarkupList, groupID) {
    this.markupList = markupList;
    this.groupID = groupID;

    let orbit = this.controls.orbit;
    let camera = this.viewer.camera;
    let height = this.viewer.height;
    let width = this.viewer.width;

    let fov = MathUtils.degToRad(camera.fov);
    let focalLength = 0.5 * height / Math.tan(0.5 * fov);

    this.target = orbit.activeTarget;
    this.position = camera.position.clone();
    this.rotation = camera.rotation.clone();
    this.windowSize = new Vector2(width, height);
    this.focalLength = focalLength;
  }

  get ready() {
    return this.boundingBox !== null;
  }

  get viewer() {
    return this.markupList.viewer;
  }

  get controls() {
    return this.viewer.controls;
  }

  /** Returns true if this is the last operation for group we belong to */
  get isLastBlock(): boolean {
    let blocks = this.markupList.allBlocks;
    let index = blocks.indexOf(this);

    if (index === (blocks.length - 1)) {
      return true;
    }

    let nextGroupID = blocks[index +1].groupID;
    if (nextGroupID !== this.groupID) {
      return true;
    }

    return false;
  }

  clear() {
    this.start.multiplyScalar(0.0);
    this.finish.multiplyScalar(0.0);
  }

  show() {
    this.selected = true;
    this.pointsHidden = false;
  }

  hide() {
    this.selected = false;
    this.pointsHidden = true;
  }

  unclassify() {
    this.selected = true;
    this.reclassifyPoints = false;
    this.newClassification = 0.0;
  }

  classify(classification) {
    this.selected = false;
    this.reclassifyPoints = true;
    this.newClassification = classification;
  }

  getClipObject(pythonExport) {
    let position = pythonExport
      ? new SceneCoordinate(this.position).toDataProjection()
      : this.position;

    let data = {
      position,
      rotation: this.rotation,
      windowSize: this.windowSize,
      focalLength: this.focalLength,
      boundingBox: this.boundingBox,
      newClassification: this.newClassification,
      pointsHidden: this.pointsHidden,
      ignorePoint: this.ignorePoint,
      reclassifyPoints: this.reclassifyPoints,
      ignoreGround: this.markupList.ignoreGround,
      highlight: this.selected,
      isLastBlock: this.isLastBlock
    };

    return data;
  }

  updateStart(u, v) {
    this.start.set(u, v);
    this.finish.copy(this.start);
  }

  updateFinish(u, v) {
    this.finish.set(u, v);

    this.boundingBox = [
      Math.min(this.start.x, this.finish.x),
      Math.min(this.start.y, this.finish.y),
      Math.max(this.start.x, this.finish.x),
      Math.max(this.start.y, this.finish.y),
    ];
  }
}

class StandardBlock extends ClippingBlock {
  constructor(markupList: PointMarkupList, groupID) {
    super(markupList, groupID);
  }
}

class EraseBlock extends ClippingBlock {
  constructor(markupList: PointMarkupList, groupID) {
    super(markupList, groupID);
    this.selected = false;
    this.ignorePoint = true;
  }

  show() {
    return;
  }

  hide() {
    return;
  }

  unclassify() {
    return;
  }

  classify() {
    return;
  }
}

export class PointMarkupList {
    private controls: PanoControls
    private setPointMarkupOperations;
    private keyDown = null;
    private mouseDown = false;
    public enabled = false;
    public ignoreGround = true;
    private groupID = 0;
    public operations: MarkupOperationInfo[] = [];
    public blocks: ClippingBlock[] = [];
    private activeBlock: ClippingBlock = null;

    constructor(controls: PanoControls, props) {
      const {setPointMarkupOperations} = props;

      this.controls = controls;
      this.setPointMarkupOperations = setPointMarkupOperations;
    }

    get viewer(): Viewer {
      return this.controls.viewer;
    }

    get width() {
      return this.viewer.width;
    }

    get height() {
      return this.viewer.height;
    }

    get selected() {
      return this.blocks.filter(box => box.selected).length > 0;
    }

    get dragTooltip() {
      return [
        t("point-markup.drag-tool-tip")
      ];
    };

    get selectTooltip() {
      return [
        t("point-markup.control-click-to-select"),
        t("point-markup.shift-click-to-unselect")
      ];
    }

    get allBlocks() {
      let blocks = [...this.blocks];
      if (this.activeBlock && this.activeBlock.ready) {
        blocks.push(this.activeBlock);
      }

      return blocks;
    }

    get canExport() {
      if (this.selected) {
        return false;
      }

      if (this.activeBlock) {
        return false;
      }

      if (this.allBlocks.length === 0) {
        return false;
      }

      return true;
    };

    getMarkupData(pythonExport) {
      return this.allBlocks.map(x => x.getClipObject(pythonExport));
    }

    undoOperation() {
      if (this.operations.length === 0) {
        return;
      }

      let data = this.operations.pop();
      let operationType = data.operation;
      let markupIDs = data.ids;

      if (operationType === MarkupOperation.SELECT) {
        this.blocks.pop();
      } else if (operationType === MarkupOperation.VISIBILITY) {
        this.decreaseGroupID();
        this.blocks.forEach(box => {
          if (markupIDs.includes(box.id)) {
            box.show();
          }
        });
      } else if (operationType === MarkupOperation.CLASSIFICATION) {
        this.decreaseGroupID();
        this.blocks.forEach(box => {
          if (markupIDs.includes(box.id)) {
            box.unclassify();
          }
        });
      } else {
        // No way to reverse. add it back to the array
        console.log("Unknown operation reversal");
        this.operations.push(data);
      }

      this.updateMarkupOperations();
    }

    reclassifySelection(classification) {
      if (this.operations.length === 0) {
        return;
      }

      let reclassifyIDs = [];
      this.blocks.forEach(box => {
        if (box.selected) {
          box.classify(classification);
          reclassifyIDs.push(box.id);
        }
      });

      this.operations.push({
        ids: reclassifyIDs,
        operation: MarkupOperation.CLASSIFICATION,
        get description() {
          return t("point-markup.update-classification-desc", {
            class: classification
          });
        }
      } as MarkupOperationInfo);

      this.updateMarkupOperations();
      this.increaseGroupID();
    }

    hideSelection() {
      if (this.operations.length === 0) {
        return;
      }

      let hiddenIDs = [];
      this.blocks.forEach(box => {
        if (box.selected) {
          box.hide();
          hiddenIDs.push(box.id);
        }
      });

      this.operations.push({
        ids: hiddenIDs,
        operation: MarkupOperation.VISIBILITY,
        get description() {
          return t("point-markup.delete-classification-desc", {
            count: hiddenIDs.length
          });
        }
      } as MarkupOperationInfo);

      this.updateMarkupOperations();
      this.increaseGroupID();
    }

    updateMarkupOperations() {
      this.setPointMarkupOperations([...this.operations]);
    }

    setState(state) {
      if (state) {
        this.enable();
      } else {
        this.disable();
      }
    }

    increaseGroupID() {
      this.groupID += 1.0;
    }

    decreaseGroupID() {
      this.groupID -= 1.0;
    }

    setIgnoreGround(state) {
      this.ignoreGround = state;
    }

    enable() {
      this.enabled = true;
      this.resetTooltip();
    }

    disable() {
      this.enabled = false;
      this.blocks = [];
      this.operations = [];
      this.activeBlock = null;
      this.updateMarkupOperations();
      clearViewerTooltip();
      this.groupID = 0;
    }

    reset() {
      this.disable();
      this.enable();
    }

    onMouseDown(event) {
      if (!this.keyDown || !this.enabled) {
        return;
      }

      this.mouseDown = true;

      if (this.keyDown === "Control") {
        this.activeBlock = new StandardBlock(this, this.groupID);
      } else if (this.keyDown === "Shift") {
        this.activeBlock = new EraseBlock(this, this.groupID);
      }

      this.activeBlock.updateStart(
        event.layerX / this.width,
        1.0 - (event.layerY / this.height)
      );

      setViewerTooltip(this.dragTooltip);
    }

    onMouseUp(event: CustomMouseEvent) {
      if (!this.enabled) {
        return false;
      }

      if (event.isLeftClick) {
        this.mouseDown = false;
        let blockCompleted = false;

        if (this.activeBlock && this.activeBlock.ready) {
          this.blocks.push(this.activeBlock);
          this.operations.push({
            ids: [this.activeBlock.id],
            operation: MarkupOperation.SELECT,
            get description() {
              return this.activeBlock instanceof EraseBlock
                ? t("point-markup.remove-from-selection")
                : t("point-markup.selected-points");
            }
          } as MarkupOperationInfo);

          blockCompleted = true;
        }

        this.activeBlock = null;
        this.resetTooltip();
        this.updateMarkupOperations();

        return blockCompleted;
      }

      return false;
    }

    onMouseMove(event) {
      if (!this.keyDown || !this.enabled || !this.mouseDown) {
        return false;
      }

      this.activeBlock.updateFinish(
        event.layerX / this.width,
        1.0 - (event.layerY / this.height)
      );

      return true;
    }

    onKeyDown(event) {
      if (!this.enabled) {
        return;
      }

      if ((event.key === "Control") || (event.key === "Shift")) {
        if (!this.keyDown) {
          this.keyDown = event.key;
          this.resetTooltip();
        }

        this.controls.setMovementState(false);
      }
    }

    onKeyUp(event) {
      if (!this.enabled) {
        return;
      }

      if (event.key === this.keyDown ) {
        this.keyDown = null;
        this.controls.setMovementState(true);
        this.resetTooltip();
      }
    }

    resetTooltip() {
      setViewerTooltip(this.selectTooltip);
    }
};
